<script setup lang="ts" generic="T extends string">
/**
 * A dropdown over a fixed option list. The trigger is not editable, but typing
 * filters the open list: printable keys build a filter (case-insensitive,
 * anywhere in the option text, prefix hits first) shown in place of the value,
 * Backspace edits it, and closing the list — Escape included — drops it. Typing
 * on the closed field opens the list with that first character as the filter, so
 * the highlight still lands on the first matching option. F4 opens the list like
 * Enter, Space and ArrowDown (BLP/nx#2833).
 *
 * Need a real input whose text is the value? That is `NxFilterSelectInput`. The
 * two share their value logic (`useSelectSelection`), their filter
 * (`filterOptionIndices`) and their dropdown body (`SelectOptionList`) — only the
 * trigger differs, which is also the reason they are separate components: this
 * one can render arbitrary markup for the selected value (a status icon), an
 * input never can.
 */
import { ChevronUpDownIcon } from '@blpde/nx-icons';
import { computed, inject, nextTick, ref, type StyleValue, watch } from 'vue';
import { NxDropdown } from '../Dropdown';
import { i18nInjectionKey } from '../internal';
import { useSelectDismiss } from './composables/selectDismiss';
import { filterOptionIndices } from './composables/selectFilter';
import { placeholderRowReachable, selectCloseAction } from './composables/selectPolicy';
import { useSelectSelection } from './composables/selectSelection';
import { FONT_SIZE_LOOKUP, InputHeight, loadingStateInjectionKey } from './internal';
import NxFakeInput from './NxFakeInput.vue';
import SelectOptionList from './SelectOptionList.vue';

const props = defineProps<{
  id?: string;
  /** The options to display */
  options: T[];
  defaultValue?: T[];
  placeholder: string;
  wrapperStyle?: StyleValue;
  height?: InputHeight;
  multiple?: boolean;
  disabled?: boolean;
  /** User can re-select the placeholder. Defaults to false. */
  allowNull?: boolean;
  /**
   * When `true`, the chevron icon is hidden until the field is hovered or
   * focused, then fades in — its space stays reserved, so nothing shifts. Useful
   * in dense layouts (e.g. table cells) to cut visual noise. Opt-in; defaults to
   * `false`.
   */
  revealIcon?: boolean;
  /** Hide the chevron entirely when an adjacent control already exposes the picker. */
  hideIcon?: boolean;
  /** Let long option labels widen the dropdown beyond the trigger. */
  fitDropdownWidth?: boolean;
  /** Keyboard shortcuts implemented by the surrounding field owner. */
  ariaKeyshortcuts?: string;
  /**
   * Accessible name of the focused trigger. Opt-in: a fallthrough `aria-label`
   * stays on the dropdown root (the anchor), where existing callers name the
   * field group. This prop is the only way to name the tab stop itself.
   * Omitted, the trigger's name stays the text it shows.
   */
  triggerAriaLabel?: string;
  /**
   * Element that describes the trigger. A fallthrough `aria-describedby` stays
   * on the root. Omitted, the trigger has no aria-describedby.
   */
  triggerAriaDescribedby?: string;
  /**
   * Leave ArrowUp/ArrowDown on the CLOSED field to the host (a grid cell hands
   * them to the row navigation). Enter, Space and typing still open the list,
   * and the open list keeps the arrows for its options. Off by default: a plain
   * form field opens on the arrows like a native select.
   */
  arrowKeysNavigate?: boolean;
  /**
   * Plain-text twin of an option: what it is MATCHED by, titled with, and — where
   * no `#option` slot overrides it — rendered as. Without it the raw option value
   * is used, which is wrong whenever the slot renders a translated label (the user
   * types what they SEE, "Zwis" for the value `subtotal`). Pass the same formatter
   * the slot uses. Defaults to `String(option)`.
   */
  optionText?: (option: T) => string;
  /**
   * What the typed filter matches an option by, when that is more than the text
   * it shows — a code next to a translated name, say (`Miete MIETE`). Defaults
   * to `optionText`. Never shown.
   */
  optionSearchText?: (option: T) => string;
  /** Keep unavailable choices visible while excluding them from selection and navigation. */
  optionDisabled?: (option: T) => boolean;
  /**
   * Group of an option, shown as a heading above its run of options („Aufgaben",
   * „Issues") — the `<optgroup>` of a native select. Headings are not options:
   * the arrow keys and the filter pass over them. The list shows the groups
   * in the order they first appear, each with all its options, so the caller
   * need not sort; `undefined` or `''` puts an option under no heading.
   */
  optionGroup?: (option: T) => string | undefined;
}>();

const [modelValue, modifier] = defineModel<T[] | [null], 'lazy'>({ required: true });

const {
  shadowValue,
  labelOf,
  optionTexts,
  triggerTitle,
  toggleOption: applyOption,
  selectPlaceholder: applyPlaceholder,
  commitIfLazy,
  revert,
} = useSelectSelection(props, modelValue, modifier);

/**
 * The options in the order the list shows them — the order every index below
 * counts in (highlight, arrow keys, typeahead, Enter). Ungrouped it IS
 * `options`; grouped, each group's options are pulled together at the place
 * the group first appears, so a heading never shows twice.
 */
const displayOptions = computed<T[]>(() => {
  const group = props.optionGroup;
  if (!group) return props.options;
  const runs = new Map<string, T[]>();
  for (const option of props.options) {
    const key = group(option) || '';
    const run = runs.get(key);
    if (run) run.push(option);
    else runs.set(key, [option]);
  }
  return [...runs.values()].flat();
});
const displayTexts = computed(() =>
  props.optionSearchText
    ? displayOptions.value.map(props.optionSearchText)
    : props.optionGroup
      ? displayOptions.value.map(labelOf)
      : optionTexts.value
);

/** What the user typed into the open list. Empty = the whole list. */
const filterText = ref('');
const isFiltering = computed(() => filterText.value.trim() !== '');

/**
 * Which shown options survive the filter, as positions in `displayOptions` —
 * also the row keys of the list, so a surviving row is patched, not rebuilt.
 * Prefix hits come first (the shared `filterOptionIndices` ordering); grouped,
 * the hits are put back into their groups' order afterwards, so a heading never
 * shows twice.
 */
const visibleIndices = computed<number[]>(() => {
  if (!isFiltering.value) return displayOptions.value.map((_, index) => index);
  const hits = filterOptionIndices(displayTexts.value, filterText.value);
  const group = props.optionGroup;
  if (!group) return hits;
  const rank = new Map<string, number>();
  for (const option of displayOptions.value) {
    const key = group(option) || '';
    if (!rank.has(key)) rank.set(key, rank.size);
  }
  const rankOf = (index: number) => rank.get(group(displayOptions.value[index]) || '') ?? 0;
  // Stable sort: prefix-first order is kept inside each group.
  return [...hits].sort((a, b) => rankOf(a) - rankOf(b));
});
/** The options the list shows — the order every index below counts in. */
const visibleOptions = computed<T[]>(() =>
  visibleIndices.value.map((i) => displayOptions.value[i])
);
const enabledIndices = computed(() =>
  visibleOptions.value
    .map((option, index) => (props.optionDisabled?.(option) ? -1 : index))
    .filter((index) => index >= 0)
);

const i18nInjection = inject(i18nInjectionKey, null);
const emptyText = computed(() =>
  isFiltering.value ? i18nInjection?.t('common.select.no_results') : undefined
);

const loadingState = inject(loadingStateInjectionKey, null);
const disabled = computed(() => {
  return props.disabled || loadingState?.status.value?.disabled;
});

const isOpen = ref<boolean>(false);
const highlightedIndex = ref<number>(-1);
const wrapperRef = ref<InstanceType<typeof NxFakeInput>>();
const triggerRef = ref<HTMLElement>();
// Structural, not `InstanceType<typeof SelectOptionList>`: the list is a generic
// `<script setup generic>` component, whose type is a function rather than a
// constructor, so `InstanceType` cannot be applied to it.
const dropdownRef = ref<{
  listElement?: HTMLElement | null;
  scrollHighlightIntoView?: () => void;
}>();

/**
 * Edit the filter. The highlight goes to the first hit (the best one: prefix hits
 * come first), or nowhere when nothing matches — the clear row is hidden while
 * filtering, so `-1` then means "no row" and Enter does nothing.
 */
function setFilter(text: string) {
  filterText.value = text;
  highlightedIndex.value = enabledIndices.value[0] ?? -1;
}

function typeIntoFilter(key: string) {
  if (disabled.value) return;
  if (isOpen.value) {
    setFilter(filterText.value + key);
  } else {
    // Opening resets the highlight via the isOpen watcher; filter after that.
    isOpen.value = true;
    nextTick(() => setFilter(key));
  }
}

// Selection logic
function toggleOption(option: T) {
  if (props.optionDisabled?.(option)) return;
  if (applyOption(option)) isOpen.value = false;
}

function selectPlaceholder() {
  applyPlaceholder();
  isOpen.value = false;
}

const { cancelled } = useSelectDismiss(isOpen, {
  fieldElement: () => wrapperRef.value?.divElement,
  listElement: () => dropdownRef.value?.listElement,
});

/**
 * Whether the clear row is on screen and can therefore be aimed at — the one
 * answer the highlight, the arrow keys and Enter all read, so they cannot
 * disagree about what `-1` means. A filter hides the row, as in the filter
 * variant.
 */
const clearRowReachable = computed(() =>
  placeholderRowReachable({
    allowNull: props.allowNull,
    multiple: props.multiple,
    filtering: isFiltering.value,
  })
);

/** Set when opening should land on a row rather than on the default. */
let requestedHighlight: number | null = null;

function resetHighlight() {
  const requested = requestedHighlight;
  requestedHighlight = null;

  if (enabledIndices.value.length === 0) {
    highlightedIndex.value = -1;
    return;
  }
  if (requested !== null) {
    highlightedIndex.value =
      enabledIndices.value.find((index) => index >= requested) ?? enabledIndices.value.at(-1)!;
    return;
  }
  highlightedIndex.value = clearRowReachable.value ? -1 : enabledIndices.value[0]!;
}

// Open/close side effects
watch(isOpen, (open) => {
  if (open) {
    resetHighlight();
    // The list's own watcher fires on a *change* of the index, so reopening at
    // the index it was left on would leave the highlighted row wherever an
    // inside scroll had pushed it. Same reason as in the filter variant.
    nextTick(() => dropdownRef.value?.scrollHighlightIntoView?.());
  } else {
    // Every close drops the filter — Escape, a pick, Tab, a click outside.
    filterText.value = '';
    // Through the same policy the filter variant uses. The filter here is only a
    // view onto the list, never the value, so it is passed as no query at all: a
    // filter typed and dropped must not clear the value. Asking the one function
    // keeps the rule identical — hand-writing it per component is what let Escape
    // mean two different things here for a whole release.
    if (
      selectCloseAction({
        cancelled: cancelled.value,
        queryEdited: false,
        query: '',
        picked: false,
        allowNull: props.allowNull,
        multiple: props.multiple,
      }) === 'revert'
    )
      revert();
    else commitIfLazy();
    // Return focus to the trigger after the popover hides (the browser may restore
    // it elsewhere otherwise) — but only for a close that happened while the focus
    // was still in this field. `NxDropdown` also closes on outside scroll, and a
    // host can close the field programmatically; grabbing the focus there would
    // pull the user out of wherever they are.
    if (wrapperRef.value?.divElement?.contains(document.activeElement)) triggerRef.value?.focus();
  }
});

// Keyboard navigation
function onKeydown(e: KeyboardEvent) {
  const modified = e.ctrlKey || e.metaKey || e.altKey;
  // Filter: a printable character (no modifier) narrows the list. Space only once
  // a filter is under way — option texts have spaces — and otherwise keeps opening
  // and selecting as before.
  const printable = e.key.length === 1 && !modified;
  if (printable && (e.key !== ' ' || (isOpen.value && filterText.value !== ''))) {
    e.preventDefault();
    typeIntoFilter(e.key);
    return;
  }
  if (!isOpen.value) {
    // F4 opens the list, as it does on a native select (BLP/nx#2833).
    if (e.key === 'F4' && !modified) {
      e.preventDefault();
      if (disabled.value) return;
      requestedHighlight = 0;
      isOpen.value = true;
      return;
    }
    const isArrow = e.key === 'ArrowDown' || e.key === 'ArrowUp';
    if (isArrow && props.arrowKeysNavigate) return;
    if (isArrow || e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      // Through the intent, not by assigning afterwards: the `isOpen` watcher runs
      // before the next render and would overwrite a direct assignment, which is
      // why opening with ArrowDown used to need a second press to leave the clear row.
      // ArrowUp opens at the bottom, like a native select and the ARIA combobox
      // pattern — landing on the first option would make a second press a dead key.
      requestedHighlight = e.key === 'ArrowUp' ? displayOptions.value.length - 1 : 0;
      isOpen.value = true;
    }
    return;
  }

  // Backspace edits the filter; with none it does nothing, as before.
  if (e.key === 'Backspace' && !modified) {
    e.preventDefault();
    if (filterText.value !== '') setFilter(filterText.value.slice(0, -1));
    return;
  }

  const maxIndex = visibleOptions.value.length - 1;

  switch (e.key) {
    case 'ArrowDown':
      e.preventDefault();
      highlightedIndex.value =
        enabledIndices.value.find((index) => index > highlightedIndex.value) ??
        highlightedIndex.value;
      break;
    case 'ArrowUp':
      e.preventDefault();
      highlightedIndex.value =
        enabledIndices.value.findLast((index) => index < highlightedIndex.value) ??
        (clearRowReachable.value ? -1 : highlightedIndex.value);
      break;
    case 'Enter':
    case ' ':
      e.preventDefault();
      if (highlightedIndex.value === -1 && clearRowReachable.value) selectPlaceholder();
      // Upper bound too: `options` can shrink while the list is open (the same
      // runtime change that makes `optionsToIndex` a computed), and an unguarded
      // index would hand `undefined` to the model as the field's value.
      else if (highlightedIndex.value >= 0 && highlightedIndex.value <= maxIndex) {
        toggleOption(visibleOptions.value[highlightedIndex.value]);
      }
      break;
    case 'Tab':
      isOpen.value = false;
      break;
  }
}

function focus(): void {
  const trigger = triggerRef.value;
  if (trigger) {
    trigger.focus();
    return;
  }
  // A host may ask for the focus in the same flush that mounts this field (a
  // lazily mounted edit cell hands the focus over right after mounting): the
  // trigger ref is assigned after the patch, so take the focus one tick later
  // instead of dropping the request.
  void nextTick(() => triggerRef.value?.focus());
}

function open(): void {
  if (disabled.value) return;
  isOpen.value = true;
  nextTick(focus);
}

defineExpose({ focus, open });
</script>

<template>
  <NxDropdown :is-open="isOpen" fit-width @close="isOpen = false">
    <template #trigger>
      <NxFakeInput
        :id="props.id"
        ref="wrapperRef"
        :wrapper-style="props.wrapperStyle"
        :icons="
          props.hideIcon
            ? []
            : [
                {
                  align: 'end',
                  component: ChevronUpDownIcon,
                  filled: true,
                  // The chevron has no own handler (the field opens the dropdown), but
                  // it is a functional affordance — `deco: false` keeps it an action so
                  // it participates in reveal-on-interaction. No `color`: NxInputWrapper
                  // drives icon colour from the `inputicon` design axis.
                  deco: false,
                  revealOnInteraction: props.revealIcon,
                },
              ]
        "
        :height="props.height"
        class="fake-input"
        :data-disabled="disabled ? true : undefined"
        @click="
          () => {
            if (!disabled) {
              isOpen = !isOpen;
              triggerRef?.focus();
            }
          }
        "
      >
        <div
          ref="triggerRef"
          :class="[
            'select-trigger',
            { placeholder: shadowValue.length === 0 && !(isOpen && filterText !== '') },
          ]"
          :title="triggerTitle"
          :tabindex="disabled ? -1 : 0"
          :aria-keyshortcuts="props.ariaKeyshortcuts"
          :aria-label="props.triggerAriaLabel"
          :aria-describedby="props.triggerAriaDescribedby"
          @keydown="onKeydown"
        >
          <!-- The filter replaces the value while it is typed: the user sees what
               narrows the list. Closing the list drops it and the value returns. -->
          <span v-if="isOpen && filterText !== ''" class="select-filter">{{ filterText }}</span>
          <template v-else-if="shadowValue.length === 0">
            <slot name="placeholder" :placeholder="props.placeholder">
              {{ props.placeholder }}
            </slot>
          </template>
          <template v-else>
            <template v-for="(option, idx) in shadowValue" :key="idx">
              <!-- `selected` renders the chosen value in the trigger only (a pill,
                   say) while the list keeps `option`; it falls back to `option`. -->
              <slot name="selected" :option="option">
                <slot name="option" :option="option">
                  {{ `${labelOf(option)}${idx < shadowValue.length - 1 ? ', ' : ''}` }}
                </slot>
              </slot>
            </template>
          </template>
        </div>
      </NxFakeInput>
    </template>
    <SelectOptionList
      ref="dropdownRef"
      v-model:highlighted-index="highlightedIndex"
      :options="visibleOptions"
      :keys="visibleIndices"
      :selected="shadowValue"
      :option-text="labelOf"
      :option-group="props.optionGroup"
      :option-disabled="props.optionDisabled"
      :multiple="props.multiple"
      :allow-null="props.allowNull"
      :show-placeholder="!isFiltering"
      :placeholder="props.placeholder"
      :empty-text="emptyText"
      :font-size="FONT_SIZE_LOOKUP[props.height ?? 's']"
      @select="toggleOption"
      @select-placeholder="selectPlaceholder"
    >
      <template #placeholder>
        <slot name="placeholder" :placeholder="props.placeholder">
          {{ props.placeholder }}
        </slot>
      </template>
      <template #option="{ option }">
        <slot name="option" :option="option">
          {{ labelOf(option) }}
        </slot>
      </template>
    </SelectOptionList>
  </NxDropdown>
</template>

<style scoped>
.select-trigger {
  display: flex;
  align-items: center;
  min-height: 1lh;
  color: var(--nxt-select-fg);
  outline: none;
  /* Clip on the trigger itself, not just on the placeholder: a long option — or a
     multi-select joining its values with ", " — would otherwise run under the
     chevron. A real <input> clips at its content edge; this trigger is a div and
     must be told to, or a long label runs past the reserved icon padding and the
     chevron paints over the text. */
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &.placeholder {
    color: var(--nxt-input-placeholder);
  }
}

/* The typed filter reads as typed text in any kit field: the field's own font
   and colour (`--nxt-select-fg`, as an input's `--nxt-input-fg`), never a
   colour of its own — in a grid cell it sat beside the row's text in the brand
   blue (I-3485). The open list with its highlighted first hit says that it
   narrows. */
.select-filter {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: pre;
  font: inherit;
  letter-spacing: inherit;
  color: inherit;
}
</style>
